import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { RouteEnding } from '../mdl/Route';
import { Util } from '../utils/Util';
import { SnapLine, SnapPoint } from '@egr/wcf/modules/core/tool';
import { VECTOR2_X, VECTOR2_Y, isParallel2, intersectLineLine2 } from '@egr/wcf/modules/core/utils/MathUtils';
export class RouteSnapping {
    constructor(pRoomManager) {
        this.mRoomManager = pRoomManager;
        this.mApp = pRoomManager.app;
        this.mSnapAxes = new Array();
        this.mSnapPoints = new Array();
        this.mActiveSnapLines = [null, null];
        this.mSnapLineFeedbacks = [null, null];
    }
    prepareSnapping(pRoute, pBaseEnding) {
        this.clear();
        this.mRoute = pRoute != null ? pRoute : null;
        if (this.mRoute != null) {
            this.mBasePoint = pBaseEnding === RouteEnding.Start ? this.mRoute.startPoint : this.mRoute.endPoint;
            let tBasePoint3d = Util.to3d(this.mBasePoint);
            this.mBasePointX = this.mBasePoint.add(VECTOR2_X);
            this.mBasePointY = this.mBasePoint.add(VECTOR2_Y);
            this.mBaseAxis = new SnapLine(Util.to3d(this.mRoute.startPoint), Util.to3d(this.mRoute.endPoint));
            this.mSnapAxes.push(this.mBaseAxis);
            //x and y axes
            this.mBaseAxisX = new SnapLine(tBasePoint3d, Util.to3d(this.mBasePointX));
            this.mSnapAxes.push(this.mBaseAxisX);
            this.mBaseAxisY = new SnapLine(tBasePoint3d, Util.to3d(this.mBasePointY));
            this.mSnapAxes.push(this.mBaseAxisY);
        }
        else {
            this.mBasePoint = null;
            this.mBasePointX = null;
            this.mBasePointY = null;
            this.mBaseAxis = null;
            this.mBaseAxisX = null;
            this.mBaseAxisY = null;
        }
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this.mRoute)
                continue;
            this.addRouteSnap(tRoute);
            this.addRouteEndingSnap(tRoute, RouteEnding.Start);
            this.addRouteEndingSnap(tRoute, RouteEnding.End);
        }
    }
    snap(pPos, pView) {
        //snap on points
        let tPoint = SnapPoint.Snap(pPos, this.mSnapPoints, pView);
        if (tPoint != null) {
            this.updateSnapFeedback(0, tPoint._snapLine1);
            this.updateSnapFeedback(1, tPoint._snapLine2);
            return (tPoint);
        }
        //snap on route endings
        let tPos2d = Util.to2d(pPos);
        for (let tRoute of this.mRoomManager.routes) {
            if (tRoute === this.mRoute)
                continue;
            let tHT = tRoute.thickness * 0.5;
            let tDist = Vector2.Distance(tPos2d, tRoute.startPoint);
            if (tDist <= tHT)
                return (Util.to3d(tRoute.startPoint));
            tDist = Vector2.Distance(tPos2d, tRoute.endPoint);
            if (tDist <= tHT)
                return (Util.to3d(tRoute.endPoint));
        }
        //snap on lines
        let tResult = SnapLine.Snap(pPos, this.mSnapAxes, pView);
        if (tResult != null) {
            this.updateSnapFeedback(0, tResult.line);
            this.updateSnapFeedback(1, null);
            return (tResult.pos);
        }
        this.updateSnapFeedback(0, null);
        this.updateSnapFeedback(1, null);
        return (pPos);
    }
    clear() {
        this.mRoute = null;
        this.mSnapAxes = new Array();
        this.mSnapPoints = new Array();
        this.updateSnapFeedback(0, null);
        this.updateSnapFeedback(1, null);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    addRouteSnap(pRoute) {
        let tSnapAxis = new SnapLine(Util.to3d(pRoute.startPoint), Util.to3d(pRoute.endPoint));
        this.mSnapAxes.push(tSnapAxis);
        //intersection with base axes
        if (this.mRoute != null) {
            if (!isParallel2(this.mRoute.startPoint, this.mRoute.endPoint, pRoute.startPoint, pRoute.endPoint)) {
                let tIsec = intersectLineLine2(this.mRoute.startPoint, this.mRoute.endPoint, pRoute.startPoint, pRoute.endPoint);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxis;
                    tSnapPoint._snapLine2 = this.mBaseAxis;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
            if (this.mBasePoint != null && this.mBasePointX != null && this.mBasePointY != null) {
                if (!isParallel2(this.mBasePoint, this.mBasePointX, pRoute.startPoint, pRoute.endPoint)) {
                    let tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointX, pRoute.startPoint, pRoute.endPoint);
                    if (tIsec != null) {
                        let tSnapPoint = Util.to3d(tIsec);
                        tSnapPoint._snapLine1 = tSnapAxis;
                        tSnapPoint._snapLine2 = this.mBaseAxisX;
                        this.mSnapPoints.push(tSnapPoint);
                    }
                }
                if (!isParallel2(this.mBasePoint, this.mBasePointY, pRoute.startPoint, pRoute.endPoint)) {
                    let tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointY, pRoute.startPoint, pRoute.endPoint);
                    if (tIsec != null) {
                        let tSnapPoint = Util.to3d(tIsec);
                        tSnapPoint._snapLine1 = tSnapAxis;
                        tSnapPoint._snapLine2 = this.mBaseAxisY;
                        this.mSnapPoints.push(tSnapPoint);
                    }
                }
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    addRouteEndingSnap(pRoute, pEnding) {
        let tPoint = pEnding === RouteEnding.Start ? pRoute.startPoint : pRoute.endPoint;
        let tPoint3d = Util.to3d(tPoint);
        let tPointX = tPoint.add(VECTOR2_X);
        let tPointY = tPoint.add(VECTOR2_Y);
        //x and y axes
        let tSnapAxisX = new SnapLine(tPoint3d, Util.to3d(tPointX));
        this.mSnapAxes.push(tSnapAxisX);
        let tSnapAxisY = new SnapLine(tPoint3d, Util.to3d(tPointY));
        this.mSnapAxes.push(tSnapAxisY);
        //ending
        this.mSnapPoints.push(Util.to3d(tPoint));
        //intersection with base axes
        if (this.mRoute != null) {
            if (!isParallel2(this.mRoute.startPoint, this.mRoute.endPoint, tPoint, tPointX)) {
                let tIsec = intersectLineLine2(this.mRoute.startPoint, this.mRoute.endPoint, tPoint, tPointX);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisX;
                    tSnapPoint._snapLine2 = this.mBaseAxis;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
            if (!isParallel2(this.mRoute.startPoint, this.mRoute.endPoint, tPoint, tPointY)) {
                let tIsec = intersectLineLine2(this.mRoute.startPoint, this.mRoute.endPoint, tPoint, tPointY);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisY;
                    tSnapPoint._snapLine2 = this.mBaseAxis;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
            if (this.mBasePoint != null && this.mBasePointX != null && this.mBasePointY != null) {
                let tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointX, tPoint, tPointY);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisY;
                    tSnapPoint._snapLine2 = this.mBaseAxisX;
                    this.mSnapPoints.push(tSnapPoint);
                }
                tIsec = intersectLineLine2(this.mBasePoint, this.mBasePointY, tPoint, tPointX);
                if (tIsec != null) {
                    let tSnapPoint = Util.to3d(tIsec);
                    tSnapPoint._snapLine1 = tSnapAxisX;
                    tSnapPoint._snapLine2 = this.mBaseAxisY;
                    this.mSnapPoints.push(tSnapPoint);
                }
            }
        }
    }
    updateSnapFeedback(pIndex, pSnapLine) {
        //dispose current feedback
        if ((pSnapLine == null || pSnapLine != this.mActiveSnapLines[pIndex]) &&
            (this.mSnapLineFeedbacks[pIndex] != null)) {
            this.mSnapLineFeedbacks[pIndex].dispose();
            this.mSnapLineFeedbacks[pIndex] = null;
        }
        //create new
        if (pSnapLine != null) {
            if (pSnapLine != this.mActiveSnapLines[pIndex]) {
                this.mActiveSnapLines[pIndex] = pSnapLine;
                this.mSnapLineFeedbacks[pIndex] = this.createSnapAxisFeedback(pSnapLine);
            }
        }
        else {
            this.mActiveSnapLines[pIndex] = null;
        }
    }
    createSnapAxisFeedback(pSnapLine) {
        if (pSnapLine == null)
            return (null);
        let tDir = pSnapLine.point2.subtract(pSnapLine.point1).normalize();
        tDir.scaleInPlace(100);
        let tLine = CreateLines("", {
            points: [pSnapLine.point1.subtract(tDir), pSnapLine.point1.add(tDir)],
            updatable: false,
            instance: null
        }, this.mApp.scene);
        tLine.isPickable = false;
        tLine.layerMask = this.feedbackVisibility;
        tLine.alpha = 0.25;
        tLine.renderingGroupId = 1;
        tLine.color = Color3.Black();
        return (tLine);
    }
}
